import React, { useMemo, useState } from 'react';
import { Home, DollarSign, TrendingUp, PiggyBank } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const initialInputs = {
  purchasePrice: '450000',
  downPaymentPercent: '20',
  mortgageRate: '6.5',
  loanTermYears: '30',
  propertyTaxRate: '1.2',
  homeInsuranceAnnual: '1200',
  maintenancePercent: '1',
  hoaMonthly: '0',
  closingCostsPercent: '3',
  appreciationRate: '3',
  sellingCostsPercent: '6',
  timeHorizonYears: '7',
  monthlyRent: '2200',
  rentIncreasePercent: '3',
  rentersInsuranceMonthly: '20',
  securityDeposit: '0',
  investmentReturnPercent: '5',
};

const formatCurrency = (value) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safeValue);
};

const calculateBuyVsRent = (inputs) => {
  const purchasePrice = Math.max(parseFloat(inputs.purchasePrice) || 0, 0);
  const downPaymentPercent = Math.max(parseFloat(inputs.downPaymentPercent) || 0, 0) / 100;
  const mortgageRate = Math.max(parseFloat(inputs.mortgageRate) || 0, 0) / 100;
  const loanTermYears = Math.max(parseFloat(inputs.loanTermYears) || 0, 0);
  const propertyTaxRate = Math.max(parseFloat(inputs.propertyTaxRate) || 0, 0) / 100;
  const homeInsuranceAnnual = Math.max(parseFloat(inputs.homeInsuranceAnnual) || 0, 0);
  const maintenancePercent = Math.max(parseFloat(inputs.maintenancePercent) || 0, 0) / 100;
  const hoaMonthly = Math.max(parseFloat(inputs.hoaMonthly) || 0, 0);
  const closingCostsPercent = Math.max(parseFloat(inputs.closingCostsPercent) || 0, 0) / 100;
  const appreciationRate = Math.max(parseFloat(inputs.appreciationRate) || 0, 0) / 100;
  const sellingCostsPercent = Math.max(parseFloat(inputs.sellingCostsPercent) || 0, 0) / 100;
  const timeHorizonYears = Math.max(parseFloat(inputs.timeHorizonYears) || 0, 0);

  const monthlyRentInitial = Math.max(parseFloat(inputs.monthlyRent) || 0, 0);
  const rentIncreasePercent = Math.max(parseFloat(inputs.rentIncreasePercent) || 0, 0) / 100;
  const rentersInsuranceMonthly = Math.max(parseFloat(inputs.rentersInsuranceMonthly) || 0, 0);
  const securityDeposit = Math.max(parseFloat(inputs.securityDeposit) || 0, 0);
  const investmentReturnPercent = Math.max(parseFloat(inputs.investmentReturnPercent) || 0, 0) / 100;

  if (purchasePrice === 0 || loanTermYears === 0 || timeHorizonYears === 0) {
    return {
      owningMonthly: 0,
      rentMonthly: monthlyRentInitial,
      totalOwningCost: 0,
      totalRentCost: 0,
      saleProceeds: 0,
      investedSavings: 0,
      netBuyCost: 0,
      netRentCost: 0,
      advantageLabel: '',
      advantageColor: '',
      summaryMessageKey: 'buyRent.summary.message.default',
    };
  }

  const downPayment = purchasePrice * downPaymentPercent;
  const loanAmount = Math.max(purchasePrice - downPayment, 0);
  const closingCosts = purchasePrice * closingCostsPercent;
  const upfrontBuyerCost = downPayment + closingCosts;

  const monthlyRate = mortgageRate / 12;
  const termMonths = loanTermYears * 12;
  let mortgagePayment = 0;
  if (loanAmount > 0) {
    if (monthlyRate > 0) {
      const factor = Math.pow(1 + monthlyRate, termMonths);
      mortgagePayment = loanAmount * ((monthlyRate * factor) / (factor - 1));
    } else {
      mortgagePayment = loanAmount / termMonths;
    }
  }

  const propertyTaxMonthly = (purchasePrice * propertyTaxRate) / 12;
  const insuranceMonthly = homeInsuranceAnnual / 12;
  const maintenanceMonthly = (purchasePrice * maintenancePercent) / 12;

  const owningMonthly = mortgagePayment + propertyTaxMonthly + insuranceMonthly + maintenanceMonthly + hoaMonthly;

  const months = Math.round(timeHorizonYears * 12);
  const monthlyReturn = Math.pow(1 + investmentReturnPercent, 1 / 12) - 1;

  let totalOwningCost = 0;
  let totalRentCost = 0;
  let investBalance = Math.max(upfrontBuyerCost - securityDeposit, 0);
  let currentMonthlyRent = monthlyRentInitial;

  for (let m = 0; m < months; m += 1) {
    const yearIndex = Math.floor(m / 12);
    if (yearIndex > 0 && m % 12 === 0) {
      currentMonthlyRent *= 1 + rentIncreasePercent;
    }

    const rentCost = currentMonthlyRent + rentersInsuranceMonthly;
    totalRentCost += rentCost;
    totalOwningCost += owningMonthly;

    const costDifference = owningMonthly - rentCost;
    investBalance = investBalance * (1 + monthlyReturn);
    if (costDifference > 0) {
      investBalance += costDifference;
    }
  }

  const monthsToConsider = Math.min(months, termMonths);
  let remainingPrincipal = 0;
  if (loanAmount > 0) {
    if (monthlyRate > 0) {
      const powTerm = Math.pow(1 + monthlyRate, termMonths);
      const powPaid = Math.pow(1 + monthlyRate, monthsToConsider);
      remainingPrincipal = months >= termMonths ? 0 : loanAmount * (powTerm - powPaid) / (powTerm - 1);
    } else {
      remainingPrincipal = Math.max(loanAmount - mortgagePayment * monthsToConsider, 0);
    }
  }

  const homeFutureValue = purchasePrice * Math.pow(1 + appreciationRate, timeHorizonYears);
  const sellingCosts = homeFutureValue * sellingCostsPercent;
  const saleProceeds = Math.max(homeFutureValue - sellingCosts - remainingPrincipal, 0);

  const totalRentCostWithDeposit = totalRentCost;
  const totalOwningWithUpfront = totalOwningCost + upfrontBuyerCost;

  const netBuyCost = totalOwningWithUpfront - saleProceeds;
  const netRentCost = totalRentCostWithDeposit - investBalance;

  let advantageLabel = '';
  let advantageColor = '';
  let summaryMessageKey = 'buyRent.summary.message.default';

  const advantage = netRentCost - netBuyCost;

  if (Number.isFinite(advantage)) {
    if (Math.abs(advantage) < 1000) {
      advantageLabel = 'even';
      advantageColor = 'bg-yellow-100 text-yellow-800';
      summaryMessageKey = 'buyRent.summary.message.even';
    } else if (advantage > 0) {
      advantageLabel = 'buy';
      advantageColor = 'bg-green-100 text-green-800';
      summaryMessageKey = 'buyRent.summary.message.buy';
    } else {
      advantageLabel = 'rent';
      advantageColor = 'bg-blue-100 text-blue-800';
      summaryMessageKey = 'buyRent.summary.message.rent';
    }
  }

  const avgRentDuringHorizon = months > 0 ? totalRentCost / months : currentMonthlyRent;

  return {
    owningMonthly,
    rentMonthly: monthlyRentInitial,
    totalOwningCost,
    totalRentCost,
    saleProceeds,
    investedSavings: investBalance,
    netBuyCost,
    netRentCost,
    advantageLabel,
    advantageColor,
    summaryMessageKey,
    upfrontBuyerCost,
    mortgagePayment,
    propertyTaxMonthly,
    insuranceMonthly,
    maintenanceMonthly,
    hoaMonthly,
    avgRentDuringHorizon,
    totalOwningWithUpfront,
    totalRentCostWithDeposit,
    advantageAmount: advantage,
  };
};

const BuyVsRent = () => {
  const [inputs, setInputs] = useState(initialInputs);
  const { t } = useTranslation();

  const results = useMemo(() => calculateBuyVsRent(inputs), [inputs]);

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Home className="h-5 w-5 text-primary-600" />
          {t('buyRent.title', 'Buy vs Rent Calculator')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('buyRent.description', 'Compare the long-term costs of buying a home versus renting. Adjust the assumptions to reflect your local market and personal plans.')}
        </p>
      </Card>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('buyRent.section.purchase', 'Purchase Details')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('buyRent.input.purchasePrice', 'Purchase Price')}
                value={inputs.purchasePrice}
                onChange={(value) => handleInputChange('purchasePrice', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('buyRent.input.downPayment', 'Down Payment (%)')}
                value={inputs.downPaymentPercent}
                onChange={(value) => handleInputChange('downPaymentPercent', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.mortgageRate', 'Mortgage Rate (%)')}
                value={inputs.mortgageRate}
                onChange={(value) => handleInputChange('mortgageRate', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.loanTerm', 'Loan Term (years)')}
                value={inputs.loanTermYears}
                onChange={(value) => handleInputChange('loanTermYears', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.closingCosts', 'Closing Costs (%)')}
                value={inputs.closingCostsPercent}
                onChange={(value) => handleInputChange('closingCostsPercent', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.appreciation', 'Home Appreciation (%)')}
                value={inputs.appreciationRate}
                onChange={(value) => handleInputChange('appreciationRate', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.sellingCosts', 'Selling Costs (%)')}
                value={inputs.sellingCostsPercent}
                onChange={(value) => handleInputChange('sellingCostsPercent', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.horizon', 'Time Horizon (years)')}
                value={inputs.timeHorizonYears}
                onChange={(value) => handleInputChange('timeHorizonYears', value)}
                type="number"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex itemsCenter gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('buyRent.section.carry', 'Carrying Costs')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('buyRent.input.propertyTax', 'Property Tax Rate (%)')}
                value={inputs.propertyTaxRate}
                onChange={(value) => handleInputChange('propertyTaxRate', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.insurance', 'Home Insurance (annual)')}
                value={inputs.homeInsuranceAnnual}
                onChange={(value) => handleInputChange('homeInsuranceAnnual', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('buyRent.input.maintenance', 'Maintenance (% of value)')}
                value={inputs.maintenancePercent}
                onChange={(value) => handleInputChange('maintenancePercent', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.hoa', 'HOA Fees (monthly)')}
                value={inputs.hoaMonthly}
                onChange={(value) => handleInputChange('hoaMonthly', value)}
                prefix="$"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <PiggyBank className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('buyRent.section.rent', 'Rent & Investment')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('buyRent.input.monthlyRent', 'Monthly Rent')}
                value={inputs.monthlyRent}
                onChange={(value) => handleInputChange('monthlyRent', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('buyRent.input.rentIncrease', 'Annual Rent Increase (%)')}
                value={inputs.rentIncreasePercent}
                onChange={(value) => handleInputChange('rentIncreasePercent', value)}
                type="number"
              />
              <CalculatorInput
                label={t('buyRent.input.rentersInsurance', "Renter's Insurance (monthly)")}
                value={inputs.rentersInsuranceMonthly}
                onChange={(value) => handleInputChange('rentersInsuranceMonthly', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('buyRent.input.securityDeposit', 'Security Deposit')}
                value={inputs.securityDeposit}
                onChange={(value) => handleInputChange('securityDeposit', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('buyRent.input.investmentReturn', 'Investment Return (%)')}
                value={inputs.investmentReturnPercent}
                onChange={(value) => handleInputChange('investmentReturnPercent', value)}
                type="number"
              />
            </div>
          </Card>
        </div>

        <div className="w-full lg:w-96 flex-shrink-0 lg:sticky lg:top-20 lg:self-start">
          <Card className="p-6 space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold uppercase tracking-wide">{t('buyRent.summary.title', 'Comparison Summary')}</h3>
            </div>
            {results.advantageLabel && (
              <>
                <div className={`rounded px-3 py-2 text-center font-semibold text-sm ${results.advantageColor}`}>
                  {t(`buyRent.summary.advantage.${results.advantageLabel}`, results.advantageLabel)}
                </div>
                <div className="text-xs text-gray-500 text-center mb-2">
                  {t(results.summaryMessageKey, results.summaryMessageKey)}
                </div>
              </>
            )}
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('buyRent.summary.owningMonthly', 'Monthly Owning Cost')}</div>
              <div className="bg-primary-100 text-primary-900 text-2xl font-bold px-4 py-2 rounded text-center">
                {formatCurrency(results.owningMonthly)}
              </div>
              <div className="text-xs text-gray-500 text-center mt-1">
                P&I {formatCurrency(results.mortgagePayment)} • {t('buyRent.summary.taxes', 'Taxes')} {formatCurrency(results.propertyTaxMonthly)} • {t('buyRent.summary.ins', 'Ins')} {formatCurrency(results.insuranceMonthly)} • {t('buyRent.summary.maint', 'Maint')} {formatCurrency(results.maintenanceMonthly)} • HOA {formatCurrency(results.hoaMonthly)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('buyRent.summary.rentMonthly', 'Starting Monthly Rent')}</div>
              <div className="text-xl font-bold text-center">
                {formatCurrency(results.rentMonthly)}
              </div>
              <div className="text-xs text-gray-500 text-center mt-1">
                {t('buyRent.summary.avgRent', 'Average rent during horizon')} {formatCurrency(results.avgRentDuringHorizon)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('buyRent.summary.totalOutlay', 'Total Cash Outlay')}</div>
              <div className="text-sm text-gray-700 dark:text-gray-300 text-center">
                {t('buyRent.summary.buyLabel', 'Buy')}: <span className="font-semibold">{formatCurrency(results.totalOwningWithUpfront)}</span> • {t('buyRent.summary.rentLabel', 'Rent')}: <span className="font-semibold">{formatCurrency(results.totalRentCostWithDeposit)}</span>
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('buyRent.summary.netCost', 'Net Cost (after equity)')}</div>
              <div className="text-sm text-gray-700 dark:text-gray-300 text-center">
                {t('buyRent.summary.buyLabel', 'Buy')}: <span className="font-semibold">{formatCurrency(results.netBuyCost)}</span> • {t('buyRent.summary.rentLabel', 'Rent')}: <span className="font-semibold">{formatCurrency(results.netRentCost)}</span>
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('buyRent.summary.advantage', 'Advantage')}</div>
              <div className="text-xl font-bold text-center">
                {formatCurrency(results.advantageAmount)}
              </div>
              <div className="text-xs text-gray-500 text-center mt-1">{t('buyRent.summary.note', 'Positive values favor buying.')}</div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BuyVsRent;
